// app/frontend/components/ui/Cards/GlassCard.tsx
import React from 'react';
import { Paper, type MantineRadius, type MantineSpacing } from '@mantine/core';
import { COLORS, GLASSMORPHISM } from '../../../src/constants/theme';
import { useIsMobile } from '../../../src/context/MediaQueryContext';

export type GlassCardAccent = 'orange' | 'success' | 'error';

export interface GlassCardProps {
  children?: React.ReactNode;
  /** Padding. Defaults to 'sm' on phones and 'md' elsewhere. */
  p?: MantineSpacing | number;
  radius?: MantineRadius;
  /** Tints the border to flag the card (e.g. a final profit, a warning). */
  accent?: GlassCardAccent;
  /** A 3px left edge in this color, the Cal-style marker (e.g. a session's room). */
  edgeColor?: string;
  /** A soft orange glow that breathes, for the one card you want picked
   *  (e.g. the recommended plan). Holds still under reduced motion. */
  glow?: boolean;
  style?: React.CSSProperties;
  className?: string;
  'data-testid'?: string;
}

const ACCENT_BORDER: Record<GlassCardAccent, string> = {
  orange: 'rgba(234, 88, 12, 0.45)',
  success: 'rgba(34, 197, 94, 0.4)',
  error: 'rgba(255, 107, 107, 0.4)',
};

const GLOW_STYLES = `
  .glass-card-glow {
    border-color: rgba(234, 88, 12, 0.75) !important;
    animation: glass-card-glow 3.2s ease-in-out infinite;
  }
  @keyframes glass-card-glow {
    0%, 100% { box-shadow: 0 0 18px rgba(234, 88, 12, 0.22), 0 0 0 1px rgba(234, 88, 12, 0.15); }
    50% { box-shadow: 0 0 38px rgba(234, 88, 12, 0.42), 0 0 0 1px rgba(234, 88, 12, 0.35); }
  }
  @media (prefers-reduced-motion: reduce) {
    .glass-card-glow { animation: none; box-shadow: 0 0 28px rgba(234, 88, 12, 0.32); }
  }
`;

// Panel surface built on the GLASSMORPHISM.card token, so panels stop
// hand-rolling sectionBg + border. The radius comes from the `radius` prop,
// not the token's fixed 12px. See #1634.
const GlassCard = React.forwardRef<HTMLDivElement, GlassCardProps>(
  ({ children, p, radius = 'md', accent, edgeColor, glow = false, style, className, 'data-testid': testId }, ref) => {
    const isMobile = useIsMobile();
    const { borderRadius: _tokenRadius, ...glass } = GLASSMORPHISM.card;

    return (
      <Paper
        ref={ref}
        p={p ?? (isMobile ? 'sm' : 'md')}
        radius={radius}
        className={[glow ? 'glass-card-glow' : '', className].filter(Boolean).join(' ') || undefined}
        data-testid={testId}
        style={{
          ...glass,
          border: `1px solid ${accent ? ACCENT_BORDER[accent] : COLORS.whiteAlpha10}`,
          ...(edgeColor ? { borderLeft: `3px solid ${edgeColor}` } : {}),
          color: COLORS.textPrimary,
          ...style,
        }}
      >
        {children}
        {glow && <style>{GLOW_STYLES}</style>}
      </Paper>
    );
  },
);

GlassCard.displayName = 'GlassCard';

export default GlassCard;
